import React, { useEffect, createContext, useMemo, useState, useContext, useCallback } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { PagingContext } from '../../contexts/paging-context';
import { FiltersContext } from '../../filters/context';
import { useQuickSearchTerm } from '../../hooks/routing/use-quick-search-term';
import { getFulfillments } from './fulfillments.api';
import {
  baseColumnsDefaultConfigsBasic,
  defaultColumnConfigurationsBasic,
  defaultOrderBy,
  baseColumnsDefaultConfigsWithSort,
  defaultColumnConfigurationsWithSort,
} from '../../store/external-fulfillments/default-column-configuration';
import Global from '../../store/global-selectors';
import { GridConfigUserSettingService } from '../../services/grid-config-user-setting-service';
import { emptyObject } from 'ui-components/default-props/empty';
import { GridConfigService } from '../../services/grid-config-service';
import { ColumnOrderByParamService } from '../../services/column-orderby-param-service';
import * as GridConfigApi from '../../store/grid-config.api';
import { mapOrdersFilterBarFiltersToContext } from '../../filters/context/map-filter-bar-filters-to-context';
import { getFiltersForViewId } from './view-to-filter-mapper';
import { useEnableExtendedFulfillmentsGridSortFilter } from '../../feature-flags/hooks/use-enable-extended-fulfillments-grid-sort-filter';
import { useSelectedShippingViewId } from '../../pages/shipping-page/shipping-left-nav/shipping-left-nav-context';

export const FULFILLMENTS_DEFAULT_FILTER_VIEW_ID = 'default';
const CUSTOM_FILTER_VIEW_ID = 'custom';
const SOURCE = 'fulfillments-filter-bar';

const FulfillmentContext = createContext();

export const FulfillmentContextProvider = ({ children = null }) => {
  const isExtendedFulfillmentsGridSortFilterEnabled = useEnableExtendedFulfillmentsGridSortFilter();
  const baseColumnsDefaultConfigs = isExtendedFulfillmentsGridSortFilterEnabled
    ? baseColumnsDefaultConfigsWithSort
    : baseColumnsDefaultConfigsBasic;
  const defaultColumnConfigurations = isExtendedFulfillmentsGridSortFilterEnabled
    ? defaultColumnConfigurationsWithSort
    : defaultColumnConfigurationsBasic;

  const { page, pageSize } = useContext(PagingContext);
  const { buildApiFilters, filters: filterContextFilters, replaceFiltersForSource, replaceFilters } = useContext(FiltersContext);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [fulfillmentIds, setFulfillmentIds] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [orderBy, setOrderBy] = useState(defaultOrderBy.orderBy);
  const [direction, setDirection] = useState(defaultOrderBy.orderByDirection);
  const [columnConfiguration, setColumnConfiguration] = useState(baseColumnsDefaultConfigs);
  const [selectedFulfillmentIds, setSelectedFulfillmentIds] = useState([]);
  const [fulfillmentIdsUpdating, setFulfillmentIdsUpdating] = useState([]);
  const [activeFilterViewId, setActiveFilterViewId] = useState(FULFILLMENTS_DEFAULT_FILTER_VIEW_ID);
  const [activeFilterView, setActiveFilterView] = useState({
    filterViewId: FULFILLMENTS_DEFAULT_FILTER_VIEW_ID,
    appliedFilters: [...filterContextFilters],
  });
  const [defaultFilterView, setDefaultFilterView] = useState({
    filterViewId: FULFILLMENTS_DEFAULT_FILTER_VIEW_ID,
    appliedFilters: [...filterContextFilters],
  });
  const [savedFilterViews, setSavedFilterViews] = useState([]);
  const [customFilterView, setCustomFilterView] = useState();
  const quickSearchTerm = useQuickSearchTerm();

  const userSettings = useSelector(Global.selectUserUserSettings);
  const statusViewId = useSelectedShippingViewId();

  const getFulfillmentsForGrid = useCallback((pageNumber, pageSizeArg, orderByColumn, orderByDirection, filters, searchTerm) => {
    setEntitiesLoading(true);
    const fulfillmentEntitiesPromise = getFulfillments({
      pageNumber,
      pageSize: pageSizeArg,
      orderBy: orderByColumn,
      direction: orderByDirection,
      filters,
      searchTerm,
    });
    fulfillmentEntitiesPromise.promise
      .then(({ ...pageData }) => {
        setFulfillmentIds(pageData.currentPageFulfillmentIds);
        setTotalRecords(pageData.totalCount);
        setEntitiesLoading(false);
      })
      .catch(() => {
        setFulfillmentIds([]);
        setTotalRecords(0);
        setEntitiesLoading(false);
      });

    return fulfillmentEntitiesPromise;
  }, []);

  const refreshGridData = useCallback(() => {
    const filters = buildApiFilters();
    const orderByParam = ColumnOrderByParamService.getFulfillmentGridOrderByApiParamOrDefault({ id: orderBy, sorted: direction }, true);
    return getFulfillmentsForGrid(page, pageSize, orderByParam.orderBy, orderByParam.orderByDirection, filters, quickSearchTerm);
  }, [page, pageSize, orderBy, direction, buildApiFilters, getFulfillmentsForGrid, quickSearchTerm]);

  const refreshColumnConfiguration = useCallback(() => {
    const storedViewUserConfigsSettings = userSettings.filter((setting) => setting.settingName.includes('v3.fulfillments.gridConfig'));
    const userViewConfigurations = storedViewUserConfigsSettings
      ? GridConfigUserSettingService.buildUserGridConfigMap(storedViewUserConfigsSettings)
      : emptyObject;
    const { [activeFilterViewId]: configForView } = userViewConfigurations[statusViewId] || {};
    if (!configForView) {
      return baseColumnsDefaultConfigs;
    }
    return GridConfigService.mergeUserAndDefaultConfigs(configForView, defaultColumnConfigurations);
  }, [userSettings, activeFilterViewId, statusViewId, baseColumnsDefaultConfigs, defaultColumnConfigurations]);

  const getSavedFilterViews = useCallback(() => {
    GridConfigApi.getAllFulfillmentsGridConfigs()
      .then((gridConfigs) => {
        setSavedFilterViews([...gridConfigs]);
      })
      .catch(() => {
        setSavedFilterViews([]);
      });
  }, []);

  useEffect(() => {
    const fulfillmentEntitiesPromise = refreshGridData();
    return () => fulfillmentEntitiesPromise?.cancel();
  }, [refreshGridData, filterContextFilters]);

  useEffect(() => {
    const newColumnConfiguration = refreshColumnConfiguration();
    setColumnConfiguration(newColumnConfiguration);
    const { sorted: initDirection, id: initOrderBy } = newColumnConfiguration.find(({ sorted }) => sorted) || {};
    setOrderBy(initOrderBy || defaultOrderBy.orderBy);
    setDirection(initDirection || defaultOrderBy.orderByDirection);
  }, [refreshColumnConfiguration]);

  useEffect(() => {
    const filters = getFiltersForViewId(statusViewId);
    const filterView = {
      filterViewId: FULFILLMENTS_DEFAULT_FILTER_VIEW_ID,
      appliedFilters: [...filters],
    };
    setDefaultFilterView(filterView);
    setActiveFilterViewId(FULFILLMENTS_DEFAULT_FILTER_VIEW_ID);
    setActiveFilterView(filterView);
    setCustomFilterView();
    replaceFilters(filters);
  }, [statusViewId, replaceFilters]);

  useEffect(() => {
    getSavedFilterViews();
  }, [getSavedFilterViews]);

  const setColumnSort = useCallback((columnId, sortDirection) => {
    setOrderBy(columnId);
    setDirection(sortDirection);
  }, []);

  const switchToCustomFilterView = useCallback((appliedFilters) => {
    setActiveFilterViewId(CUSTOM_FILTER_VIEW_ID);
    setCustomFilterView({
      filterViewId: CUSTOM_FILTER_VIEW_ID,
      appliedFilters: [...appliedFilters],
    });
  }, []);

  const resetCustomFilterView = useCallback(() => {
    setActiveFilterViewId(FULFILLMENTS_DEFAULT_FILTER_VIEW_ID);
    setActiveFilterView(defaultFilterView);
    setCustomFilterView();
  }, [defaultFilterView]);

  const switchActiveFilterView = useCallback(
    (filterViewId) => {
      const filterViewsForCurrentGrid = savedFilterViews.filter((view) => view.gridViewId === statusViewId);
      if (customFilterView) {
        filterViewsForCurrentGrid.push(customFilterView);
      }
      const newFilterView = filterViewsForCurrentGrid.find((view) => view.filterViewId === filterViewId);
      if (newFilterView) {
        setActiveFilterViewId(newFilterView.filterViewId);
        setActiveFilterView(newFilterView);
        const newContextFilters = mapOrdersFilterBarFiltersToContext(newFilterView.appliedFilters, SOURCE);
        replaceFiltersForSource(SOURCE, newContextFilters);
      } else {
        setActiveFilterViewId(FULFILLMENTS_DEFAULT_FILTER_VIEW_ID);
        setActiveFilterView(defaultFilterView);
        replaceFilters(defaultFilterView.appliedFilters);
      }
    },
    [customFilterView, defaultFilterView, savedFilterViews, statusViewId, replaceFiltersForSource, replaceFilters],
  );

  const saveFilterView = useCallback(
    (filterView) => {
      setSavedFilterViews([...savedFilterViews.filter((view) => view.filterViewId !== filterView.filterViewId), filterView]);
      setActiveFilterViewId(filterView.filterViewId);
      setActiveFilterView(filterView);
      setCustomFilterView();
    },
    [savedFilterViews],
  );

  const deleteFilterView = useCallback(
    (filterViewToDelete) => {
      setSavedFilterViews(savedFilterViews.filter((savedView) => savedView.filterViewId !== filterViewToDelete.filterViewId));
      setActiveFilterViewId(FULFILLMENTS_DEFAULT_FILTER_VIEW_ID);
      setActiveFilterView(defaultFilterView);
      replaceFilters(defaultFilterView.appliedFilters);
    },
    [defaultFilterView, savedFilterViews, replaceFilters],
  );

  const value = useMemo(
    () => ({
      entitiesLoading,
      fulfillmentIds,
      totalRecords,
      setColumnSort,
      selectedFulfillmentIds,
      setSelectedFulfillmentIds,
      refreshGridData,
      fulfillmentIdsUpdating,
      setFulfillmentIdsUpdating,
      columnConfiguration,
      setColumnConfiguration,
      quickSearchTerm,
      orderBy,
      direction,
      activeFilterViewId,
      defaultFilterView,
      customFilterView,
      savedFilterViews,
      activeFilterView,
      switchToCustomFilterView,
      resetCustomFilterView,
      switchActiveFilterView,
      saveFilterView,
      deleteFilterView,
    }),
    [
      entitiesLoading,
      fulfillmentIds,
      totalRecords,
      setColumnSort,
      selectedFulfillmentIds,
      setSelectedFulfillmentIds,
      refreshGridData,
      fulfillmentIdsUpdating,
      setFulfillmentIdsUpdating,
      columnConfiguration,
      setColumnConfiguration,
      quickSearchTerm,
      orderBy,
      direction,
      activeFilterViewId,
      defaultFilterView,
      customFilterView,
      savedFilterViews,
      activeFilterView,
      switchToCustomFilterView,
      resetCustomFilterView,
      switchActiveFilterView,
      saveFilterView,
      deleteFilterView,
    ],
  );

  return <FulfillmentContext.Provider value={value}>{children}</FulfillmentContext.Provider>;
};

FulfillmentContextProvider.propTypes = {
  children: PropTypes.node,
};

export { FulfillmentContext };
